import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AssistRail } from '../../../components/OfficeWorkspace/AssistRail';
import { OfficeWorkspace } from '../../../components/OfficeWorkspace/OfficeWorkspace';
import { toContainerRef, useResponseWorkspace } from '../../../components/OfficeWorkspace/useOfficeWorkspace';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { rfiSectionClient, useRfiPeople, useRfiSections } from '../rfiSectionHooks';
import { useRfiRecord } from './RfiRecordContext';
/**
 * Capability statement (WRT-07 page 1): a section of the response document
 * (role capability_statement) edited in OfficeWorkspace. Without one, "Start
 * the capability statement" creates it and inserts its Heading 1 at the start
 * of the document. With writing_workspace_v1 off the page links to /legacy.
 */ export function RfiCapabilityStatementPage() {
    const { id = '' } = useParams();
    const record = useRfiRecord();
    const writingOn = useFeatureFlag('writing_workspace_v1') === true;
    const pangaeaId = record?.pangaeaId ?? '';
    const { data: sections = [], isLoading } = useRfiSections(writingOn ? pangaeaId : '');
    const { data: people = [] } = useRfiPeople();
    const container = {
        kind: 'rfi',
        rfiResponseProjectId: pangaeaId
    };
    const workspace = useResponseWorkspace(container, undefined);
    const qc = useQueryClient();
    const start = useMutation({
        // TODO(WRT-35): pass the suggested placement (section | separate_document).
        mutationFn: ()=>rfiSectionClient.ensureRfiCapabilityStatement({
                rfiResponseProjectId: pangaeaId,
                placement: 'section'
            }),
        onSuccess: ()=>Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'rfi-sections',
                        pangaeaId
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeWorkspace'
                    ]
                })
            ])
    });
    if (!writingOn) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                asChild: true,
                size: "sm",
                variant: "secondary",
                children: /*#__PURE__*/ _jsx(Link, {
                    to: `/rfi-response/${id}/legacy`,
                    children: "Open today's workspace"
                })
            }),
            className: "m-[var(--space-6)]",
            description: "The capability statement is edited in the response document.",
            framed: true,
            title: "Capability statement"
        });
    }
    const cap = sections.find((s)=>s.role === 'capability_statement');
    if (!isLoading && !cap) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "p-[var(--space-6)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiWorkspace.capabilityStart,
                        disabled: start.isPending || !pangaeaId,
                        onClick: ()=>start.mutate(),
                        size: "sm",
                        children: "Start the capability statement"
                    }),
                    description: "It goes at the start of the response document as its own section.",
                    framed: true,
                    title: "No capability statement yet"
                }),
                start.error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: start.error.message
                }) : null
            ]
        });
    }
    const owner = people.find((p)=>p.userId === cap?.ownerUserId);
    const documentId = workspace.data?.activeDocumentId || record?.responseDocumentId;
    return /*#__PURE__*/ _jsx("div", {
        className: "flex h-full min-h-0 flex-col",
        children: /*#__PURE__*/ _jsx(OfficeWorkspace, {
            anchor: cap?.anchorBookmark || undefined,
            container: container,
            cta: null,
            documentId: documentId || undefined,
            header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                meta: owner ? `Owner: ${owner.name || owner.email}` : undefined,
                title: "Capability statement",
                titleSize: "ui"
            }),
            sideTabs: {
                assist: ({ activeDocumentId })=>/*#__PURE__*/ _jsx(AssistRail, {
                        canEdit: workspace.data?.capabilities?.canEdit ?? false,
                        documentId: activeDocumentId,
                        projectId: record?.zouProjectId || undefined,
                        responseContainer: toContainerRef(container),
                        rfiResponseProjectId: pangaeaId,
                        sectionAnchor: cap?.anchorBookmark || undefined,
                        surface: "rfi_response",
                        unitNoun: "section"
                    })
            },
            unitNoun: "section"
        })
    });
}
